import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type { MarMedicationResponse } from '../../../services/api';

interface MarEntryActionsProps {
  entry: MarMedicationResponse;
  /** Whether the record can be edited/deleted (visit is open, not signed). */
  canEdit: boolean;
  /** Explanation shown in a tooltip when editing is blocked. */
  disabledReason: string;
  onEdit: (entry: MarMedicationResponse) => void;
  onDelete: (entry: MarMedicationResponse) => void;
  /** Icon size (defaults to h-4 w-4); the row table uses a smaller h-3.5). */
  iconClassName?: string;
}

/**
 * Edit/delete buttons for a single MAR entry. When the visit is no longer
 * editable the buttons are disabled and a tooltip explains why (mirrors the
 * server's editable-visit guard).
 */
const MarEntryActions: React.FC<MarEntryActionsProps> = ({
  entry,
  canEdit,
  disabledReason,
  onEdit,
  onDelete,
  iconClassName = 'h-4 w-4',
}) => {
  if (!canEdit) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex items-center gap-1' tabIndex={0}>
              <Button
                variant='ghost'
                size='sm'
                disabled
                aria-label='Edit administration record'
              >
                <Pencil className={`${iconClassName} text-muted-foreground`} />
              </Button>
              <Button
                variant='ghost'
                size='sm'
                disabled
                aria-label='Delete administration record'
              >
                <Trash2 className={`${iconClassName} text-muted-foreground`} />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className='max-w-xs'>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className='flex items-center gap-1'>
      <Button
        variant='ghost'
        size='sm'
        aria-label='Edit administration record'
        onClick={() => onEdit(entry)}
      >
        <Pencil className={iconClassName} />
      </Button>
      <Button
        variant='ghost'
        size='sm'
        aria-label='Delete administration record'
        onClick={() => onDelete(entry)}
      >
        <Trash2 className={`${iconClassName} text-destructive`} />
      </Button>
    </div>
  );
};

export default MarEntryActions;
